import { useState, FormEvent } from "react";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import { UMRAH_PACKAGES } from "../../mocks/umrah";
import SeoMeta from "../../components/base/SeoMeta";

const BASE_URL = (import.meta.env.VITE_SITE_URL as string) || "https://flightfare.pk";

const UMRAH_JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Affordable Umrah Packages from Lahore Pakistan – flightfare.pk",
    "url": `${BASE_URL}/umrah`,
    "description": "Book affordable Umrah packages from Lahore with flightfare.pk. Economy, 3-star, 4-star and 5-star Umrah packages with flights, hotels and Ziyarat."
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Umrah Packages from Pakistan",
    "serviceType": "Religious Travel",
    "provider": {
      "@type": "TravelAgency",
      "name": "flightfare.pk",
      "url": BASE_URL,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Bahria Town, Lahore",
        "addressRegion": "Punjab",
        "addressCountry": "PK"
      },
      "telephone": "+923711432451"
    },
    "areaServed": "PK",
    "description": "Comprehensive Umrah packages from Pakistan including round-trip flights, accommodation near Haram, visa processing and Ziyarat tours. Economy to 5-star packages available.",
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "PKR",
      "availability": "https://schema.org/InStock"
    },
    "termsOfService": `${BASE_URL}/umrah`
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is included in flightfare.pk Umrah packages?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our Umrah packages from Pakistan include round-trip flights, comfortable hotel accommodation near the Haram in Makkah and Madinah, Umrah visa processing, guided Ziyarat tours, and 24/7 support throughout your pilgrimage journey."
        }
      },
      {
        "@type": "Question",
        "name": "How long are the Umrah packages from Lahore?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our Umrah packages range from 7 nights to 21 nights depending on your chosen package. We offer flexible durations to suit your schedule and budget."
        }
      },
      {
        "@type": "Question",
        "name": "Can I get a customized Umrah package from flightfare.pk?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! flightfare.pk offers fully customized Umrah packages. You can select your preferred dates, hotel star rating, number of pilgrims, and special requirements. Contact us at +923711432451 or fill our custom package form."
        }
      }
    ]
  }
];

interface CustomPackageModalProps {
  onClose: () => void;
}

function CustomPackageModal({ onClose }: CustomPackageModalProps) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", packageType: "Economy", departureDate: "", pilgrims: "1", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.city.trim()) { setError("Please fill all required fields."); return; }
    if (form.message.length > 500) { setError("Message must be 500 characters or less."); return; }
    setError("");
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(form).forEach(([k, v]) => body.append(k, v));
      const res = await fetch("https://readdy.ai/api/form/d7240ehlum2co2q1g1r0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) { setSubmitted(true); } else { setError("Something went wrong. Please try again."); }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">Make Customized Umrah Package</h2>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        {submitted ? (
          <div className="p-10 text-center">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 bg-green-100 rounded-full">
              <i className="ri-check-line text-3xl text-green-600"></i>
            </div>
            <p className="text-gray-900 font-semibold text-lg">Request Submitted!</p>
            <p className="text-gray-500 text-sm mt-1">Our team will contact you shortly with a customised Umrah package.</p>
            <button type="button" onClick={onClose} className="mt-5 px-6 py-2 bg-cyan-600 text-white text-sm font-semibold rounded-md hover:bg-cyan-700 transition-colors cursor-pointer whitespace-nowrap">
              Close
            </button>
          </div>
        ) : (
          <form data-readdy-form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Full Name <span className="text-red-400">*</span></label>
                <input name="name" type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Your name" className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-cyan-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Phone <span className="text-red-400">*</span></label>
                <input name="phone" type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+92 3XX XXXXXXX" className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-cyan-400 transition-colors" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
                <input name="email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="your@email.com" className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-cyan-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">City <span className="text-red-400">*</span></label>
                <input name="city" type="text" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="Your city" className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-cyan-400 transition-colors" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Package Type</label>
                <select name="packageType" value={form.packageType} onChange={e => setForm(f => ({ ...f, packageType: e.target.value }))} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-cyan-400 transition-colors cursor-pointer bg-white appearance-none">
                  {["Economy", "3 Star", "4 Star", "5 Star"].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">No. of Pilgrims</label>
                <select name="pilgrims" value={form.pilgrims} onChange={e => setForm(f => ({ ...f, pilgrims: e.target.value }))} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-cyan-400 transition-colors cursor-pointer bg-white appearance-none">
                  {["1","2","3","4","5","6","7","8","9","10+"].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Preferred Departure Date</label>
              <input name="departureDate" type="date" value={form.departureDate} onChange={e => setForm(f => ({ ...f, departureDate: e.target.value }))} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-cyan-400 transition-colors cursor-pointer" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Additional Requirements</label>
              <textarea name="message" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Any special requirements or questions..." rows={3} maxLength={500} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-cyan-400 transition-colors resize-none" />
              <p className="text-xs text-gray-400 text-right mt-0.5">{form.message.length}/500</p>
            </div>
            {error && <p className="text-red-500 text-xs">{error}</p>}
            <button type="submit" disabled={submitting} className="w-full py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-bold rounded-md transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
              {submitting ? "Submitting..." : "SEND REQUEST"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function UmrahPage() {
  const [showModal, setShowModal] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <SeoMeta
        title="Affordable Umrah Packages from Lahore Pakistan – Economy to 5 Star | flightfare.pk"
        description="Book affordable Umrah packages from Lahore with flightfare.pk. Economy, 3-star, 4-star and 5-star packages with flights, hotels near Haram, Umrah visa and Ziyarat included. Call 03711432451."
        keywords="Umrah packages from Pakistan, Umrah packages Lahore, affordable Umrah 2024, cheap Umrah packages Pakistan, Umrah packages from Lahore economy, 5 star Umrah package Pakistan"
        canonical="/umrah"
        jsonLd={UMRAH_JSON_LD}
      />

      {/* Hero Banner */}
      <div className="pt-16">
        <div className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0891b2 0%, #06b6d4 40%, #22d3ee 70%, #a3e8ef 100%)" }}>
          <div className="absolute top-0 left-0 right-0 h-2 bg-amber-400" />
          <div className="absolute top-6 right-24 w-10 h-10 rounded-full border-2 border-white/30" />
          <div className="absolute top-16 right-64 w-5 h-5 rounded-full border border-white/20" />
          <div className="absolute bottom-8 left-16 w-8 h-8 rounded-full border border-white/20" />
          <div className="absolute bottom-16 left-48 w-4 h-4 rounded-full border border-white/30" />
          <div className="max-w-7xl mx-auto px-4 py-12 md:py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="max-w-xl">
              <h1 className="text-3xl font-extrabold text-white leading-tight tracking-wide uppercase">
                Affordable Umrah Packages
              </h1>
              <p className="text-white/90 text-sm mt-3 leading-relaxed">
                Explore our affordable and comprehensive Umrah packages from Lahore. We offer personalised Umrah services to our clients.
              </p>
              <button
                type="button"
                onClick={() => setShowModal(true)}
                className="mt-6 px-6 py-2.5 bg-cyan-700 hover:bg-cyan-800 text-white text-sm font-bold rounded-md transition-colors cursor-pointer whitespace-nowrap"
              >
                MAKE CUSTOMIZED UMRAH PACKAGE
              </button>
            </div>
            <div className="hidden md:block flex-shrink-0">
              <img
                src="https://public.readdy.ai/ai/img_res/7f3e909f6ba39853541327ae844ff362.jpg"
                alt="Umrah Packages"
                className="w-64 h-52 object-contain object-top"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Explore Packages */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">Explore Packages</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
            {UMRAH_PACKAGES.map((pkg) => (
              <a
                key={pkg.slug}
                href={`/umrah/${pkg.slug}`}
                className="block rounded-lg overflow-hidden border border-gray-200 cursor-pointer hover:border-cyan-300 transition-all group"
              >
                <div className="w-full h-44 relative">
                  <img
                    src={pkg.heroImage}
                    alt={pkg.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-white text-sm font-extrabold tracking-widest uppercase">UMRAH</p>
                      <p className="text-white text-sm font-extrabold tracking-widest uppercase">PACKAGE</p>
                      <span className={`inline-block mt-2 px-3 py-0.5 ${pkg.labelColor} text-white text-xs font-bold rounded-full uppercase tracking-wider`}>
                        {pkg.label}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-sm font-bold text-gray-900 mb-1">{pkg.title}</p>
                  <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 mb-2">{pkg.subtitle}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">{pkg.nights}N / {pkg.days}D</span>
                    <span className="text-xs text-cyan-600 font-semibold flex items-center gap-0.5">
                      View <i className="ri-arrow-right-line text-xs"></i>
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-10 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 space-y-4">
          <p className="text-sm text-gray-600 leading-relaxed">
            Experience a spiritually enriching Umrah journey with flightfare.pk&apos;s affordable and comprehensive Umrah packages from Pakistan. We are committed to providing a seamless and memorable pilgrimage experience, offering a variety of packages tailored to meet your needs and budget.
          </p>
          <p className="text-sm text-gray-600 leading-relaxed">
            Our packages include round-trip flights, comfortable accommodations close to the holy sites, and guided Ziyarat tours to ensure that every aspect of your journey is taken care of with the utmost care and precision. Our team of experienced travel experts assists you with every detail, from visa processing to itinerary planning, so you can focus entirely on your spiritual journey.
          </p>
        </div>
      </section>

      {showModal && <CustomPackageModal onClose={() => setShowModal(false)} />}
      <Footer />
    </div>
  );
}
